import { forwardRef, useMemo } from 'react';

import { styled } from '@mui/material';

import capitalize from 'lodash/capitalize';

import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerProps,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';

import {
  CatalogueItem,
  interpolateTemplate,
  toNotificationSeverity,
} from './constants';
import { LabelChips } from './_components/LabelChips';
import { SeverityChip } from '../_components';
import { CopyToClipboard } from 'components/clickable-actions';

type Props = DrawerProps & {
  item: CatalogueItem;
};

const KeyLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.P12R,
  color: tokens.theme.fg.structural.secondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}));

const KeyDetailRow = ({
  label,
  children,
}: {
  label: string;
  children: any;
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    gap={16}
  >
    <KeyLabel>{label}:</KeyLabel>
    <Stack
      direction="row"
      alignItems="center"
      gap={4}
      justifyContent="flex-end"
      flexWrap="wrap"
    >
      {children}
    </Stack>
  </Stack>
);

const WhatHappens = ({ item }: { item: CatalogueItem }) => {
  const summary = useMemo(
    () => interpolateTemplate(item.template),
    [item.template]
  );

  return (
    <Stack gap={8}>
      <Typography variant="P14B">What happens</Typography>
      <Typography
        variant="P12R"
        sx={{ whiteSpace: 'pre-line' }}
        color={tokens.theme.fg.structural.secondary}
      >
        {item.description || summary}
      </Typography>
      {item.description && summary && item.description !== summary && (
        <Typography
          variant="P12R"
          sx={{ whiteSpace: 'pre-line' }}
          color={tokens.theme.fg.structural.secondary}
        >
          {summary}
        </Typography>
      )}
    </Stack>
  );
};

/* Key-detail values mirror the Notification/Event-log drawer canon:
   P12M values, 14px copy icons. */
const KeyDetails = ({ item }: { item: CatalogueItem }) => {
  const mappedSeverity = toNotificationSeverity(item.severity);

  return (
    <Stack gap={12}>
      <Typography variant="P14B">Key details</Typography>
      <KeyDetailRow label="Severity">
        {mappedSeverity ? (
          <SeverityChip severity={mappedSeverity} size="small" />
        ) : (
          <Typography variant="P12M">{capitalize(item.severity)}</Typography>
        )}
      </KeyDetailRow>
      <KeyDetailRow label="Domain">
        <Typography variant="P12M">{capitalize(item.domain)}</Typography>
      </KeyDetailRow>
      <KeyDetailRow label="Definition ID">
        <Typography variant="P12M" noWrap>
          {item.id}
        </Typography>
        <CopyToClipboard text={item.id} iconSize={14} />
      </KeyDetailRow>
      {item.labels.length > 0 && (
        <KeyDetailRow label="Labels">
          <LabelChips labels={item.labels} />
        </KeyDetailRow>
      )}
    </Stack>
  );
};

const DefinitionDrawer = forwardRef<HTMLDivElement, Props>(
  ({ item, ...drawerProps }, ref) => (
    <Drawer
      {...drawerProps}
      ref={ref}
      testId="notification-definition-drawer"
      size="medium"
    >
      {/* Same header treatment as the event log drawer: absolute DS close
            button, L10M secondary caption, h5 title. */}
      <DrawerHeader closeButtonProps={{ positioned: 'absolute' }} mt={42}>
        <Stack gap={4} flex={1}>
          <Typography
            variant="L10M"
            color={tokens.theme.fg.structural.secondary}
            sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            Notification definition
          </Typography>
          <Typography variant="h5">{item.name}</Typography>
        </Stack>
      </DrawerHeader>
      <DrawerBody>
        <DrawerContent px={24}>
          <Stack py={24} gap={24}>
            <WhatHappens item={item} />
            <Divider />
            <KeyDetails item={item} />
          </Stack>
        </DrawerContent>
      </DrawerBody>
    </Drawer>
  )
);

DefinitionDrawer.displayName = 'DefinitionDrawer';

export { DefinitionDrawer };
